import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import {
  getRenderTXCenterTableCell,
  TX_CENTER_TABLE_COLUMNS,
  TX_CENTERS_LIST_FILTERS_BASE,
  TXCentersFilters,
} from '@lp/features/admin/tables/TXCentersListTable';
import { AuthContext } from '@lp/core/auth';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useTXCentersListQuery } from '@/queries/tx-centers';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useWrappedRemoveTxCenterMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

export const TXCentersList = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: TX_CENTERS_LIST_FILTERS_BASE,
    query: useTXCentersListQuery,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.TxCenterCreate);

  const {
    isOpen,
    isLoading: isTxCenterDeleting,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveTxCenterMutation,
    successMessage: 'TX Center deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/customers/tx_centers/create');
  };

  const handleEdit = useCallback(
    (hospitalId: string) => {
      navigate(`/admin/customers/tx_centers/${hospitalId}`);
    },
    [navigate],
  );

  const { resolveSubdivisionName } = useCountriesData();
  const renderTXCenterTableCell = useMemo(() => {
    return getRenderTXCenterTableCell(handleEdit, onModalOpen, resolveSubdivisionName);
  }, [handleEdit, onModalOpen, resolveSubdivisionName]);

  const TX_CENTER_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions
      ? TX_CENTER_TABLE_COLUMNS
      : TX_CENTER_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="TX Centers">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <TXCentersFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="TX Centers"
            columns={TX_CENTER_TABLE_COLUMNS_FILTERED}
            renderCell={renderTXCenterTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete TX Center?"
        description="Are you sure you want to delete the TX Center?"
        isOpen={isOpen}
        isLoading={isTxCenterDeleting}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
